import type { UiNode, UiNodeInputAttributes, UiText } from '@ory/client';

import ActionButton from '@/components/Button/ActionButton';
import ResponseMessage from '@/components/Error/ResponseMessage';
import { Spacer, VStack } from '@/components/Layout';
import { useNodeTranslation } from '@/features/auth/hooks/useNodeTranslation';

const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  microsoft: 'Microsoft',
  // google: "Google",
  // apple: "Apple",
};

function getProviderDisplayName(provider: string): string {
  return (
    PROVIDER_DISPLAY_NAMES[provider] ??
    provider.charAt(0).toUpperCase() + provider.slice(1)
  );
}

/**
 * Kratos は context.provider を小文字で返すため、表示用にキャピタライズする。
 * @ory/client の UiText.context は object 型のため、Record にキャストして参照する。
 */
function capitalizeProviderInLabel(label: UiText): UiText {
  const ctx = label.context as Record<string, unknown> | undefined;
  const provider = ctx?.provider;
  if (typeof provider !== 'string') return label;

  return {
    ...label,
    context: {
      ...label.context,
      provider: getProviderDisplayName(provider),
    } as object,
  };
}

type Props = {
  node: UiNode;
  disabled?: boolean;
};

/**
 * OIDC 専用の submit ボタンコンポーネント
 *
 * 外部 IdP へのリダイレクトであることを視覚的に区別するため、
 * outline バリアントで描画し、プロバイダー名をキャピタライズする。
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#submit-input-node
 */
export function OidcSubmitInput({ node, disabled }: Props) {
  const { translateUiText } = useNodeTranslation();
  const attrs = node.attributes as UiNodeInputAttributes;

  if (attrs.type !== 'submit') {
    return null;
  }

  const labelUiText = node.meta?.label
    ? capitalizeProviderInLabel(node.meta.label)
    : undefined;
  const label = labelUiText ? translateUiText(labelUiText) : undefined;
  const errorMessages = node.messages
    ?.filter((msg) => msg.type === 'error')
    .map((msg) => translateUiText(msg));

  return (
    <VStack className="w-full gap-0">
      <ActionButton
        type="submit"
        name={attrs.name}
        value={attrs.value as string}
        disabled={disabled ?? attrs.disabled}
        variant="default"
        className="w-full"
      >
        {label}
      </ActionButton>
      {errorMessages && errorMessages.length > 0 && (
        <>
          <Spacer variant="xxs" />
          <ResponseMessage type="error" size="xs">
            {errorMessages[0]}
          </ResponseMessage>
        </>
      )}
    </VStack>
  );
}
